<script lang="ts" setup>
import type { TranslatableString } from '~/types/widgets/widgetTypes'
import getWidgetTextTranslation from '~/utils/getWidgetTextTranslation'
import getWidgetTranslatedMediaUrl from '~/utils/getWidgetTranslatedMediaUrl'

import TabContent from './TabContent.vue'

interface Props {
  backgroundColor?: string
  category: {
    name: TranslatableString
    logo?: TranslatableString
    storeLogos: {
      logo: TranslatableString
      url: string
    }[]
    storeList: {
      name: string
      url: string
      vpnOnly: boolean
    }[]
  }[]
}

const active = ref(0)
const props = defineProps<Props>()
</script>

<template>
  <WidgetLayout>
    <div class="store-tabs">
      <UTabs
        v-model="active"
        :items="props.category"
        class="w-full"
        :ui="{
          wrapper: 'tabs',
          base: 'tab tab-body',
          container: '!mt-0',
          list: {
            base: 'grid',
            height: 'h-auto',
            background: '!bg-transparent',
            marker: {
              wrapper: 'tab-marker',
              background: 'bg-quoteYellow dark:bg-dmGray2',
            },
            tab: {
              base: 'tab-title',
              active: '-active',
            },
          },
        }">
        <template #default="{ item }">
          <span class="flex gap-1 items-center text-text1 dark:text-dmText1">
            <img
              v-if="item.logo"
              :src="getWidgetTranslatedMediaUrl(item.logo)"
              height="24"
              class="h-6 w-auto dark:invert"
              :alt="getWidgetTextTranslation(item.name)" />
            <span class="hidden md:block">
              {{ getWidgetTextTranslation(item.name) }}
            </span>
          </span>
        </template>
        <template #item="{ item, index }">
          <div class="mt-5" />

          <TabContent
            :key="index"
            :store-list="item.storeList?.sort((a, b) => a.name.localeCompare(b.name))"
            :store-logos="item.storeLogos" />
        </template>
      </UTabs>
    </div>
  </WidgetLayout>
</template>

<style lang="postcss" scoped>
.store-tabs {
  :deep(.tabs) {
    padding: 0;
    color: theme('colors.text1');
    background: transparent;

    ::-webkit-scrollbar {
      height: 2px;
      background: theme('colors.quoteYellow');
    }

    .tab {
      border: 0;
      border-radius: 0;
      background: transparent;

      .tab-body {
        border: 0;
        padding: 0;
      }
    }

    .tab-marker {
      border-radius: theme('vars.borderRadiusHeavy');
    }

    .tab-title {
      padding: 0.75rem 1rem;
      min-height: 52px;
    }
  }
}
</style>
